/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/assistantPrefillBridge.ts
 * PURPOSE: Cross-shell bridge so layouts can open the Langflow drawer while `AiAssistantPanel` consumes
 *          a one-shot composer prefill (e.g. “Ask AI” on text selection or dashboard chart suggestions).
 * RECENT: [2.2.720] — Docs sync (**`CHANGELOG` [2.2.720]**); session widget context survives panel close (**[2.2.717]**).
 * RECENT: [2.2.717] — session-scoped **`_activeWidgetContext`**; **`setActiveWidgetContext`** / **`getActiveWidgetContext`** (no localStorage).
 * RECENT: [2.2.716] — assistant prefill contextWidgetId; optional **`widgetTitle`** on payload for chart context banner above composer.
 */

const EVENT = 'brazn-assistant-open-with-prefill';

/** Pending payload cleared on **`consumeAssistantPendingPrefill`** (single consumer). */
export type AssistantPrefillPayload = {
  markdown: string;
  contextWidgetId?: string;
  widgetTitle?: string;
  /** When true, **`AiAssistantPanel`** sends the prompt immediately after open (advisor/coaching chips). */
  autoSend?: boolean;
};

let pending: AssistantPrefillPayload | null = null;

/** Survives panel close or remount so the chart context banner can restore on re-open (session tab only). */
let _activeWidgetContext: { contextWidgetId: string; widgetTitle: string } | null = null;

export function setActiveWidgetContext(ctx: { contextWidgetId: string; widgetTitle: string } | null): void {
  _activeWidgetContext = ctx;
}

export function getActiveWidgetContext(): { contextWidgetId: string; widgetTitle: string } | null {
  return _activeWidgetContext;
}

export function dispatchAssistantOpenWithPrefill(
  markdown: string,
  options?: { contextWidgetId?: string; widgetTitle?: string; autoSend?: boolean }
): void {
  pending = {
    markdown,
    contextWidgetId: options?.contextWidgetId,
    widgetTitle: options?.widgetTitle,
    autoSend: options?.autoSend,
  };
  const wid = options?.contextWidgetId?.trim();
  if (wid) {
    setActiveWidgetContext({
      contextWidgetId: wid,
      widgetTitle: options?.widgetTitle ?? '',
    });
  }
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent(EVENT));
  }
}

/** Returns **`null`** if nothing was pending (already consumed or never dispatched). */
export function consumeAssistantPendingPrefill(): AssistantPrefillPayload | null {
  const out = pending;
  pending = null;
  return out;
}

export function subscribeAssistantOpenWithPrefill(handler: () => void): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }
  const h = () => handler();
  document.addEventListener(EVENT, h);
  return () => document.removeEventListener(EVENT, h);
}
